import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";
const AuthContext = createContext(null);

async function fetchWithRetry(fn, maxRetries = 3, baseDelay = 1000) {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await fn();
      return result;
    } catch (err) {
      const isNetworkError =
        err?.message === "Failed to fetch" ||
        err?.message?.includes("NetworkError") ||
        err?.message?.includes("network") ||
        err?.name === "TypeError";

      if (isNetworkError && attempt < maxRetries) {
        const delay = baseDelay * Math.pow(2, attempt);
        console.warn(`Network error, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }
      throw err;
    }
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const retryTimerRef = useRef(null);

  const clearSession = useCallback(() => {
    setUser(null);
    setProfile(null);
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith("expertconnect-auth") || key.startsWith("sb-")) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.warn("Failed to clear localStorage:", e);
    }
  }, []);

  const fetchProfile = useCallback(async (userId, retry = true) => {
    try {
      const result = await fetchWithRetry(async () => {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", userId)
          .maybeSingle();

        if (error) {
          if (error.message === "Failed to fetch" || error.code === "PGRST000") {
            throw new TypeError("Failed to fetch");
          }
          console.warn("Profile query error:", error);
          return null;
        }

        return data;
      }, retry ? 3 : 0);

      if (result) {
        setProfile(result);
        return result;
      }

      if (!result) {
        console.warn("Profile not found for user:", userId);
      }
    } catch (err) {
      console.warn("Profile fetch failed after retries:", err?.message);

      if (retry) {
        retryTimerRef.current = setTimeout(() => {
          console.info("Background retry: fetching profile...");
          fetchProfile(userId, false).catch(() => {});
        }, 10000);
      }
    }
    return null;
  }, []);

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.warn("Session retrieval error:", error.message);
          if (mounted) {
            clearSession();
            setLoading(false);
          }
          return;
        }

        if (session?.user && mounted) {
          setUser(session.user);
          setProfile({
            id: session.user.id,
            email: session.user.email,
            name: session.user.user_metadata?.name || "",
            role: session.user.user_metadata?.role || "client"
          });
          fetchProfile(session.user.id).catch(console.error);
        }
      } catch (err) {
        console.warn("Auth init failed:", err);
        if (mounted) {
          clearSession();
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        if (event === "TOKEN_REFRESHED" && session?.user) {
          setUser(session.user);
          return;
        }

        if (event === "SIGNED_OUT" || !session?.user) {
          clearSession();
          return;
        }

        if (event === "SIGNED_IN" && session?.user) {
          setUser(session.user);
          setProfile({
            id: session.user.id,
            email: session.user.email,
            name: session.user.user_metadata?.name || "",
            role: session.user.user_metadata?.role || "client"
          });
          fetchProfile(session.user.id).catch(console.error);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
    };
  }, [fetchProfile, clearSession]);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      throw error;
    }

    setUser(data.user);
    setProfile({
      id: data.user.id,
      email: data.user.email,
      name: data.user.user_metadata?.name || "",
      role: data.user.user_metadata?.role || "client"
    });

    const fullProfile = await fetchProfile(data.user.id);
    return { user: data.user, profile: fullProfile };
  };

  const register = async (email, password, name, role) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name, role }
      }
    });

    if (error) {
      throw error;
    }

    if (data.user) {
      setUser(data.user);
      setProfile({
        id: data.user.id,
        email: data.user.email,
        name,
        role
      });
      fetchProfile(data.user.id).catch(console.error);
    }

    return data;
  };

  const saveConsents = async (userId, email, { acceptTerms, acceptNewsletter }) => {
    try {
      if (acceptTerms) {
        await supabase.from("user_consents").insert({
          user_id: userId,
          consent_type: "terms_and_privacy",
          accepted: true
        });
      }

      if (acceptNewsletter) {
        await supabase.from("newsletter_subscriptions").insert({
          user_id: userId,
          email,
          subscribed: true,
          source: "registration"
        });
      }
    } catch (err) {
      console.warn("Failed to save consents:", err);
    }
  };

  const logout = async () => {
    clearSession();
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch (err) {
      console.warn("Sign out request failed:", err);
    }
  };

  const resetPassword = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });
    if (error) {
      throw error;
    }
  };

  const verifyEmailOtp = async (email, token) => {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: "email"
    });

    if (error) {
      throw error;
    }

    if (data.user) {
      setUser(data.user);
      setProfile({
        id: data.user.id,
        email: data.user.email,
        name: data.user.user_metadata?.name || "",
        role: data.user.user_metadata?.role || "client"
      });
      fetchProfile(data.user.id).catch(console.error);
    }

    return data;
  };

  const updateUser = async (updates) => {
    if (!profile) return;

    const { error } = await supabase
      .from("profiles")
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq("id", profile.id);

    if (!error) {
      setProfile((prev) => ({ ...prev, ...updates }));
    }
  };

  const combinedUser = profile
    ? {
      id: profile.id,
      email: profile.email,
      name: profile.name,
      role: profile.role,
      avatar_url: profile.avatar_url || "",
      title: profile.title || "",
      bio: profile.bio || "",
      hourly_rate: profile.hourly_rate || 0,
      specializations: profile.specializations || [],
      companies: profile.companies || [],
      achievements: profile.achievements || [],
      available: profile.available !== undefined ? profile.available : true,
      client_type: profile.client_type || "",
      job_title: profile.job_title || "",
      company_name: profile.company_name || "",
      company_website: profile.company_website || "",
      company_size: profile.company_size || "",
      company_description: profile.company_description || "",
      industry: profile.industry || "",
      freelance_services: profile.freelance_services || [],
      client_onboarding_completed: profile.client_onboarding_completed || false
    }
    : null;

  return (
     <AuthContext.Provider value={{ user: combinedUser, supabaseUser: user, login, register, logout, resetPassword, updateUser, verifyEmailOtp, saveConsents, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
